import React from 'react';
import { View, Modal, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import PasswordInput from './PasswordInput';
import FormActions from './FormActions';
import { WindowInformation } from '../services/window-information';


interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: (password: string) => Promise<'success' | 'failure'>;
    onCancel?: () => void;
    itemName?: string;
    customMessage?: string;
    customConfirm?: string;
}

export default function ConfirmIdentityModal({
    isModalVisible,
    setModalVisible,
    onSubmit,
    onCancel,
}: ModalProps) {
    const { styles } = useStyles(styleSheet);

    const [password, setPassword] = React.useState('');
    const windowInfo = WindowInformation();

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            if (password == '') {
                return;
            }
            onConfirm();
        }
    }

    async function onConfirm() {
        try {
            const res = await onSubmit(password);
            if (res === 'success') {
                setModalVisible(false);
                setPassword('');
            }
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onCancel && onCancel();
                setModalVisible(false);
                setPassword('');
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={styles.body(windowInfo.isMobile)}>
                    <View style={{ gap: 16, justifyContent: 'center', alignItems: 'center' }}>
                        <MyAppText style={styles.headerText}>{translate('confirmYourIdentity')}</MyAppText>
                        <MyAppText style={styles.contentText}>{translate('confirmYourIdentityText')}</MyAppText>
                    </View>

                    <PasswordInput
                        label={translate('accessPassword')}
                        placeholder={translate('typeYourPassword')}
                        value={password}
                        onChangeText={setPassword}
                        invalid={() => password === ''}
                        onKeyPress={handleKeyDown}
                    />
                    <FormActions
                        confirmText='confirm'
                        onSubmit={() => onConfirm()}
                        onClose={() => {
                            onCancel && onCancel();
                            setModalVisible(false);
                            setPassword('');
                        }}
                        disabled={password === ''}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    body: (isMobile: boolean) => {
        return {
            backgroundColor: theme.colors.white['500'],
            flexDirection: 'column',
            borderRadius: 8,
            padding: 32,
            rowGap: 25,
            maxWidth: isMobile ? '95%' : 400
        };
    },
    headerText: {
        fontSize: 18,
        color: theme.colors.neutral['500'],
        fontWeight: '600'
    },
    contentText: {
        fontSize: 16,
        color: theme.colors.neutral['400'],
    }
}));
